import * as React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";

function FullPageSpinner() {
  const [showEscape, setShowEscape] = React.useState(false);
  React.useEffect(() => {
    const t = window.setTimeout(() => setShowEscape(true), 3000);
    return () => window.clearTimeout(t);
  }, []);

  function nukeAndReload() {
    try {
      localStorage.removeItem("arabise-auth");
      // Also nuke any chunked auth cookies leftover from a broken session.
      document.cookie.split(";").forEach(c => {
        const eq = c.indexOf("=");
        const name = (eq > -1 ? c.slice(0, eq) : c).trim();
        if (name.startsWith("sb-")) {
          document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
        }
      });
    } catch { /* ignore */ }
    window.location.assign("/#/login");
    window.location.reload();
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg text-fg-3">
      <div className="flex flex-col items-center gap-4">
        <div className="flex items-center gap-3 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-accent" />
          Loading…
        </div>
        {showEscape && (
          <button type="button" onClick={nukeAndReload}
            className="text-xs text-fg-3 hover:text-fg underline-offset-2 hover:underline">
            Stuck? Clear session and reload
          </button>
        )}
      </div>
    </div>
  );
}

/** Redirects to /login when no session. */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loading, session } = useAuth();
  const loc = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!session) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <>{children}</>;
}

/**
 * Stricter guard for study surfaces:
 *   - no session              → /login
 *   - deactivated profile     → signOut + /signed-out
 *   - license_none            → /subscribe
 *   - onboarding incomplete   → /onboarding
 */
export function RequireAccess({ children }: { children: React.ReactNode }) {
  const { loading, session, access, signOut } = useAuth();
  const loc = useLocation();

  React.useEffect(() => {
    if (!loading && access?.is_deactivated) {
      void signOut("/signed-out");
    }
  }, [loading, access, signOut]);

  if (loading) return <FullPageSpinner />;
  if (!session) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (!access)  return <FullPageSpinner />;

  if (access.is_deactivated) return <FullPageSpinner />;
  if (!access.profile.onboarding_complete) {
    return <Navigate to="/onboarding" replace state={{ from: loc.pathname }} />;
  }
  if (access.needs_subscribe) {
    return <Navigate to="/subscribe" replace state={{ from: loc.pathname }} />;
  }
  return <>{children}</>;
}

/** Bounce signed-in users away from auth pages (login/onboarding). */
export function RedirectIfAuthed({ children, to = "/dashboard" }: { children: React.ReactNode; to?: string }) {
  const { loading, session, access } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (session && access?.profile.onboarding_complete && !access.is_deactivated && !access.needs_subscribe) {
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}
